import { useSetAtom } from 'jotai';
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from 'packages/lib/utils/hooks/use-query';
import {
  NumberFilters,
  searchPageNoAtom,
  searchPageSizeAtom,
  SearchQuery,
  searchQueryAtom,
  SelectFilters,
  TextFilters,
  ToggleFilters,
} from 'packages/omnitag/state/text-search';
import { useGetFiltersListAPI } from 'packages/omnitag/api/use-get-filters-api';
import { selectedResultsAtom } from 'packages/omnitag/state';
import { useResetAtom } from 'jotai/utils';

// Aug-11-2025 - Used Gemini 2.5 Agent to make filters dynamic (affected useSyncRouteParams)
// Aug-20-2025 - Used Gemini 2.5 Agent to add support for 'number' filters
// Oct-30-2025 - Used Gemini 2.5 Agent to add support for 'DateRange' filters

/**  Converts query params used for search text, filters, and pagination into a SearchQuery set in searchQueryAtom
 */
export const useSyncRouteParams = (): SearchQuery => {
  const filtersSWR = useGetFiltersListAPI();
  const { query } = useParams<{ query?: string }>();
  const searchTextDecoded = decodeURIComponent(query ?? '')?.replace(
    /\+/g,
    ' '
  );
  const q = useQuery();
  const setSearchQuery = useSetAtom(searchQueryAtom);
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();
  const setPageNo = useSetAtom(searchPageNoAtom);
  const setPageSize = useSetAtom(searchPageSizeAtom);

  const dynamicFilters = useMemo(
    () => filtersSWR.data?.filters ?? [],
    [filtersSWR.data]
  );

  // Set any filter defaults in the URL on app launch (after dynamicFilters have loaded)
  useEffect(() => {
    if (
      location.pathname === '/polaris/tools' ||
      location.pathname === '/polaris/data-requests'
    ) {
      return;
    }

    const params = new URLSearchParams(location.search);
    let paramsChanged = false;

    for (const filter of dynamicFilters) {
      if (
        (filter as any).defaultValue !== undefined &&
        !params.has(filter.paramName)
      ) {
        params.set(filter.paramName, String((filter as any).defaultValue));
        paramsChanged = true;
      }
    }

    if (paramsChanged) {
      const encodedText = encodeURIComponent(searchTextDecoded ?? '').replace(
        /%20/g,
        '+'
      );
      history.push({
        pathname: generatePath(match.path, {
          query: encodedText ? encodedText : undefined,
        }),
        search: params.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicFilters]);

  // Read pageNo/pageSize from URL on app load, if applicable
  useEffect(() => {
    if (
      location.pathname === '/polaris/tools' ||
      location.pathname === '/polaris/data-requests'
    ) {
      return;
    }

    const pageNo = q.get('pageNo');
    const pageSize = q.get('pageSize');

    if (pageNo) setPageNo(parseInt(pageNo));
    if (pageSize) setPageSize(parseInt(pageSize));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // WARNING: since 'q' is in deps array, this object changes when ANY query param changes, not just the ones used for search!
  const { searchQueryFromURL, searchParamsStrNoPagination } = useMemo(() => {
    const pageNo = q.get('pageNo');
    const pageSize = q.get('pageSize');

    // Bug fix: q.getAll() returns an array, and if this array is included in a deps array, it will change every render,
    // triggering an infinite loop. We can't include these in deps arrays. q is memoized so ok to include in deps array.
    const logNames = q.getAll('logNames');

    // Warning: do NOT use this object in a deps array, as it will change any time ANY URL param changes, not just search params.
    // Use of arrays (q.getAll) also triggers re-render.
    const searchQueryFromURL = {
      text: searchTextDecoded,
      logNames,
      pageNo: pageNo ? parseInt(pageNo) : undefined,
      pageSize: pageSize ? parseInt(pageSize) : undefined,
    } as Partial<SearchQuery>;

    for (const filter of dynamicFilters) {
      if (filter.filterType === 'select') {
        (searchQueryFromURL as unknown as SelectFilters)[filter.paramName] =
          q.getAll(filter.paramName);
      } else if (filter.filterType === 'text') {
        if (filter.isMulti) {
          (searchQueryFromURL as unknown as TextFilters)[filter.paramName] = q
            .getAll(filter.paramName)
            .join(',');
        } else {
          (searchQueryFromURL as unknown as TextFilters)[filter.paramName] =
            q.get(filter.paramName) || '';
        }
      } else if (filter.filterType === 'number') {
        const value = q.get(filter.paramName);
        (searchQueryFromURL as unknown as NumberFilters)[filter.paramName] =
          value ? parseFloat(value) : null;
      } else if (filter.filterType === 'toggle') {
        const value = q.get(filter.paramName);
        (searchQueryFromURL as unknown as ToggleFilters)[filter.paramName] =
          value === 'true';
      } else if (filter.filterType === 'dateRange') {
        const from = q.get(`${filter.paramName}From`);
        const to = q.get(`${filter.paramName}To`);
        (searchQueryFromURL as any)[`${filter.paramName}From`] = from;
        (searchQueryFromURL as any)[`${filter.paramName}To`] = to;
      }
    }

    // We can't put searchQueryFromURL in a deps array that resets search state, as it's re-created any
    // time ANY URL param changes. But we CAN watch a stringified version, and the string changes only when
    // the params we're interested in change.
    const searchQueryNoPagination = { ...searchQueryFromURL };
    // Don't include pageNo/pageSize, as this string is used to trigger resetSearchState(), and including pagination
    // would result in an infinite loop of changing pageNo to 1, updating the url, etc.
    delete searchQueryNoPagination.pageNo;
    delete searchQueryNoPagination.pageSize;
    const searchParamsStrNoPagination = JSON.stringify(searchQueryNoPagination);

    return { searchQueryFromURL, searchParamsStrNoPagination };
  }, [searchTextDecoded, q, dynamicFilters]);

  useEffect(() => {
    if (
      location.pathname === '/polaris/tools' ||
      location.pathname === '/polaris/data-requests'
    ) {
      return;
    }
    setSearchQuery(searchQueryFromURL as SearchQuery);
  }, [searchQueryFromURL, location, setSearchQuery]);

  // We must watch a stringified version of just the params we're interested in, as the object
  // would be changed anytime ANY param changes, not just search params.
  // However, we only want to reset selectedResults when certain search params change.
  const resetSelectedResults = useResetAtom(selectedResultsAtom);
  const prevSearchParamsRef = useRef<string>();

  useEffect(() => {
    if (
      location.pathname === '/polaris/tools' ||
      location.pathname === '/polaris/data-requests'
    ) {
      return;
    }

    const prevParams = prevSearchParamsRef.current
      ? (JSON.parse(prevSearchParamsRef.current) as Partial<SearchQuery>)
      : null;
    const currentParams = JSON.parse(
      searchParamsStrNoPagination
    ) as Partial<SearchQuery>;
    const hasLoadedParams = Object.keys(currentParams).length > 2;

    if (hasLoadedParams) {
      prevSearchParamsRef.current = searchParamsStrNoPagination;
    }

    const hasLoadedTextSearch =
      (currentParams?.text?.length ?? 0) > (prevParams?.text?.length ?? 0);

    if (
      prevParams &&
      currentParams &&
      hasLoadedParams &&
      !hasLoadedTextSearch &&
      (prevParams.text !== currentParams.text ||
        JSON.stringify(prevParams.logNames) !==
          JSON.stringify(currentParams.logNames))
    ) {
      resetSelectedResults();
    }
    prevSearchParamsRef.current = searchParamsStrNoPagination;
  }, [resetSelectedResults, searchParamsStrNoPagination, location.pathname]);

  return searchQueryFromURL as SearchQuery;
};
